import { supabase } from './supabaseService';
import { logger } from '../utils/logger';
import {
  UserRole,
  Permission,
  RolePermission,
  UserInvitation,
  PublicInvitation,
  UserWithRole,
  RoleName,
  PermissionCheck
} from '../types/roles';

export const rolesService = {
  async getAllRoles(): Promise<UserRole[]> {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getRoleById(roleId: string): Promise<UserRole | null> {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .eq('id', roleId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getRoleByName(roleName: RoleName): Promise<UserRole | null> {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .eq('name', roleName)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getAllPermissions(): Promise<Permission[]> {
    const { data, error } = await supabase
      .from('permissions')
      .select('*')
      .order('module, action');

    if (error) throw error;
    return data || [];
  },

  async getPermissionsByRole(roleId: string): Promise<Permission[]> {
    const { data, error } = await supabase
      .from('role_permissions')
      .select(`
        permission_id,
        permissions (*)
      `)
      .eq('role_id', roleId);

    if (error) throw error;
    return data?.map((rp: any) => rp.permissions) || [];
  },

  async getUserPermissions(userId: string): Promise<Permission[]> {
    const { data, error } = await supabase
      .rpc('get_user_permissions', { user_id_param: userId });

    if (error) throw error;

    return data?.map((p: any) => ({
      code: p.permission_code,
      module: p.permission_module,
      action: p.permission_action
    })) || [];
  },

  async checkUserPermission(userId: string, permissionCode: string): Promise<boolean> {
    const { data, error } = await supabase
      .rpc('user_has_permission', {
        user_id_param: userId,
        permission_code_param: permissionCode
      });

    if (error) throw error;
    return data || false;
  },

  async isUserCollaborator(userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .rpc('user_is_collaborator', { user_id_param: userId });

    if (error) throw error;
    return data || false;
  },

  async getUserWithRole(userId: string): Promise<UserWithRole | null> {
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        user_role:user_roles (*)
      `)
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async updateUserRole(userId: string, roleId: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({ role_id: roleId })
      .eq('id', userId);

    if (error) throw error;
  },

  async createInvitation(
    email: string,
    collaboratorId: string,
    roleId: string,
    invitedBy: string
  ): Promise<{ invitationToken: string; invitationId: string }> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No hay sesión activa. Por favor inicia sesión nuevamente.');
      }

      logger.debug('Enviando invitación a Edge Function');

      const response = await fetch(`${supabaseUrl}/functions/v1/invite_collaborator`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Client-Info': 'crm-ceroideas'
        },
        body: JSON.stringify({
          email,
          collaborator_id: collaboratorId,
          role_id: roleId,
          invited_by: invitedBy
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        logger.error('Respuesta no válida de Edge Function', { preview: text.substring(0, 200) });
        throw new Error(
          'El servidor devolvió una respuesta inválida. La función Edge puede no estar desplegada correctamente. ' +
          'Por favor contacta al administrador del sistema.'
        );
      }

      const result = await response.json();

      if (!response.ok) {
        logger.error('Error en respuesta de Edge Function', { status: response.status, error: result.error });
        throw new Error(result.error || `Error del servidor: ${response.status}`);
      }

      if (!result.success) {
        logger.error('Error creando invitación', { error: result.error, details: result.details, errorType: result.errorType });
        throw new Error(result.error || result.details || 'No se pudo crear la invitación');
      }

      logger.debug('Invitación creada exitosamente');

      return {
        invitationToken: result.data.invitation_token,
        invitationId: result.data.invitation_id
      };
    } catch (error) {
      logger.error('Error en createInvitation', error);

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet e intenta de nuevo.');
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Error desconocido al crear la invitación');
    }
  },

  async getPublicInvitationByToken(token: string): Promise<PublicInvitation | null> {
    // Pre-login lookup — goes through get_public_invitation SECURITY DEFINER
    // because RLS on user_invitations only lets admins SELECT.
    const { data, error } = await supabase.rpc('get_public_invitation', { p_token: token });

    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return row ? (row as PublicInvitation) : null;
  },

  async getPendingInvitations(): Promise<UserInvitation[]> {
    const { data, error } = await supabase
      .from('user_invitations')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async cancelInvitation(invitationId: string): Promise<void> {
    const { error } = await supabase
      .from('user_invitations')
      .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
      .eq('id', invitationId);

    if (error) throw error;
  },

  async acceptInvitation(
    token: string,
    password: string,
    _name: string
  ): Promise<{ success: boolean; userId?: string; error?: string }> {
    // Edge Function expects {token, password, confirm_password}. The name
    // typed in the form is not forwarded — accept_invitation derives it from
    // users.name (via collaborator_id) so the invitee cannot rename someone
    // else's row. Keep _name in the signature for now to avoid churning all
    // callers.
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/accept_invitation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${supabaseKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token,
        password,
        confirm_password: password
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || 'Failed to accept invitation'
      };
    }

    const result = await response.json();
    return result;
  },

  async grantCRMAccess(
    userId: string,
    roleId: string,
    grantedBy: string
  ): Promise<void> {
    console.log('🔐 Otorgando acceso CRM:', { userId, roleId, grantedBy });

    const { error } = await supabase
      .from('users')
      .update({
        role_id: roleId,
        has_crm_access: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) {
      console.error('❌ Error otorgando acceso CRM:', error);
      throw error;
    }

    console.log('✅ Acceso CRM otorgado correctamente');
  },

  async revokeCRMAccess(
    userId: string,
    revokedBy: string
  ): Promise<void> {
    const { error } = await supabase
      .from('users')
      .update({
        has_crm_access: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) throw error;
  },

  async getUserWithCRMAccess(userId: string): Promise<any> {
    const { data, error } = await supabase
      .from('users')
      .select(`
        id,
        name,
        email,
        role,
        role_id,
        is_active,
        has_crm_access,
        user_role:user_roles (*)
      `)
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  hasPermission(permissions: Permission[], module: string, action: string): boolean {
    return permissions.some(
      p => p.module === module && p.action === action
    );
  },

  hasAnyPermission(permissions: Permission[], checks: Array<{ module: string; action: string }>): boolean {
    return checks.some(({ module, action }) =>
      permissions.some(p => p.module === module && p.action === action)
    );
  },

  hasAllPermissions(permissions: Permission[], checks: Array<{ module: string; action: string }>): boolean {
    return checks.every(({ module, action }) =>
      permissions.some(p => p.module === module && p.action === action)
    );
  }
};
